import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useHistory } from "react-router-dom";
import API_BASE from "../config/api";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

const areaLabels = {
  landing: "Public pages",
  auth: "Auth funnel",
  borrower: "Borrower dashboard",
  dashboard: "Dashboard",
};

const dateFieldSx = {
  width: 160,
  background: "#fff",
  "& .MuiInputBase-input": {
    color: "#102a43",
  },
  "& .MuiInputLabel-root": {
    color: "#52606d",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#cbd5e1",
  },
  "& input::-webkit-calendar-picker-indicator": {
    opacity: 0.75,
    filter:
      "invert(18%) sepia(17%) saturate(1149%) hue-rotate(169deg) brightness(91%) contrast(90%)",
  },
};

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatSeconds(value) {
  const seconds = Number(value || 0);
  if (seconds < 60) return `${seconds.toFixed(seconds % 1 ? 1 : 0)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(Number(value || 0) % 1 ? 1 : 0)}%`;
}

function dateInputValue(date = new Date()) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
}

function formatPayload(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
}

function PayloadBlock({ value }) {
  return (
    <pre
      style={{
        maxWidth: 360,
        maxHeight: 180,
        margin: 0,
        overflow: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        color: "#243b53",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 6,
        padding: 10,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 12,
      }}
    >
      {formatPayload(value)}
    </pre>
  );
}

function DataTable({ columns, rows, emptyText = "No data yet." }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: "Poppins, sans-serif",
          fontSize: 13,
        }}
      >
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                style={{
                  textAlign: "left",
                  padding: "10px 12px",
                  borderBottom: "1px solid #d9e2ec",
                  color: "#52606d",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: "16px 12px", color: "#7b8794" }}
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={`${row.page_path || row.target_label || index}-${index}`}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{
                      padding: "10px 12px",
                      borderBottom: "1px solid #edf2f7",
                      color: "#243b53",
                      verticalAlign: "top",
                    }}
                  >
                    {column.render ? column.render(row) : row[column.key] || "-"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 24 }}>
      <Typography
        variant="h6"
        style={{
          color: "#102a43",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 800,
          fontSize: 18,
          marginBottom: 10,
        }}
      >
        {title}
      </Typography>
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #d9e2ec",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </section>
  );
}

function MetricCard({ label, value, hint }) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #d9e2ec",
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div style={{ color: "#627d98", fontSize: 12, fontWeight: 700 }}>
        {label}
      </div>
      <div
        style={{
          color: "#102a43",
          fontSize: 24,
          fontWeight: 900,
          marginTop: 6,
        }}
      >
        {value}
      </div>
      {hint ? (
        <div style={{ color: "#7b8794", fontSize: 12, marginTop: 6 }}>{hint}</div>
      ) : null}
    </div>
  );
}

function PercentBar({ value }) {
  const percent = Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <div
      style={{
        height: 8,
        background: "#e6f0fb",
        borderRadius: 999,
        overflow: "hidden",
        minWidth: 110,
      }}
    >
      <div
        style={{
          width: `${percent}%`,
          height: "100%",
          background: "#0e5aa5",
          borderRadius: 999,
        }}
      />
    </div>
  );
}

function FlowColumn({ title, rows, emptyText }) {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: 14,
        minHeight: 180,
      }}
    >
      <div
        style={{
          color: "#102a43",
          fontWeight: 800,
          fontSize: 13,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {rows?.length ? (
        rows.slice(0, 5).map((row) => (
          <div key={`${title}-${row.page_path}`} style={{ marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                color: "#243b53",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <span>{row.page_label || row.page_path}</span>
              <span>{formatPercent(row.percent)}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <PercentBar value={row.percent} />
              <span style={{ color: "#627d98", fontSize: 12 }}>
                {formatNumber(row.sessions)} sessions
              </span>
            </div>
          </div>
        ))
      ) : (
        <div style={{ color: "#7b8794", fontSize: 13 }}>{emptyText}</div>
      )}
    </div>
  );
}

function DashboardBehaviorTab({ dashboard }) {
  const totals = dashboard?.totals || {};
  const productRows = dashboard?.product_exploration || [];
  const entryRows = dashboard?.entry_pages || [];
  const nextRows = dashboard?.next_steps || [];
  const lastRows = dashboard?.last_seen_pages || [];
  const transitionRows = dashboard?.transitions || [];
  const pageRows = dashboard?.page_stats || [];
  const clickRows = dashboard?.top_clicks || [];
  const sectionRows = dashboard?.sections || [];

  return (
    <>
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(170px, 1fr))"
        gap="12px"
        marginTop="24px"
      >
        <MetricCard label="Dashboard sessions" value={formatNumber(totals.sessions)} />
        <MetricCard label="Known users" value={formatNumber(totals.users)} />
        <MetricCard
          label="Explored other pages"
          value={formatPercent(totals.explored_other_pages_percent)}
          hint={`${formatNumber(totals.explored_other_pages_sessions)} sessions`}
        />
        <MetricCard label="Dashboard page views" value={formatNumber(totals.page_views)} />
        <MetricCard label="Dashboard clicks" value={formatNumber(totals.clicks)} />
        <MetricCard label="Avg. dashboard time" value={formatSeconds(totals.avg_time_seconds)} />
      </Box>

      <Section title="Dashboard Flow">
        <div style={{ padding: 16 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
              alignItems: "stretch",
            }}
          >
            <FlowColumn
              title="Entry page"
              rows={entryRows}
              emptyText="No dashboard entry pages yet."
            />
            <FlowColumn
              title="Next page after dashboard"
              rows={nextRows}
              emptyText="No second dashboard page seen yet."
            />
            <FlowColumn
              title="Last point seen"
              rows={lastRows}
              emptyText="No abandoned dashboard sessions older than 30 minutes."
            />
          </div>
        </div>
      </Section>

      <Section title="Product Exploration">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 12,
            padding: 16,
          }}
        >
          {productRows.map((row) => (
            <div
              key={row.key}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: 14,
                background: "#fbfdff",
              }}
            >
              <div style={{ color: "#102a43", fontWeight: 800 }}>
                {row.label}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <PercentBar value={row.percent} />
                <strong style={{ color: "#0e5aa5" }}>{formatPercent(row.percent)}</strong>
              </div>
              <div style={{ color: "#627d98", fontSize: 12, marginTop: 8 }}>
                {formatNumber(row.sessions)} dashboard sessions
              </div>
            </div>
          ))}
          {!productRows.length ? (
            <div style={{ color: "#7b8794" }}>No product exploration data yet.</div>
          ) : null}
        </div>
      </Section>

      <Section title="Most Common Dashboard Moves">
        <DataTable
          rows={transitionRows}
          emptyText="No repeated dashboard page-to-page moves yet."
          columns={[
            { key: "from_label", label: "From" },
            { key: "to_label", label: "To" },
            {
              key: "sessions",
              label: "Sessions",
              render: (row) => formatNumber(row.sessions),
            },
            {
              key: "percent",
              label: "Share",
              render: (row) => formatPercent(row.percent),
            },
          ]}
        />
      </Section>

      <Section title="Dashboard Pages To Investigate">
        <DataTable
          rows={pageRows}
          columns={[
            { key: "page_label", label: "Page" },
            { key: "page_path", label: "Path" },
            {
              key: "page_views",
              label: "Views",
              render: (row) => formatNumber(row.page_views),
            },
            {
              key: "clicks",
              label: "Clicks",
              render: (row) => formatNumber(row.clicks),
            },
            {
              key: "exits",
              label: "Exits",
              render: (row) => formatNumber(row.exits),
            },
            {
              key: "avg_time_seconds",
              label: "Avg. time",
              render: (row) => formatSeconds(row.avg_time_seconds),
            },
            {
              key: "avg_scroll_depth",
              label: "Avg. scroll",
              render: (row) =>
                row.avg_scroll_depth ? `${Math.round(row.avg_scroll_depth)}%` : "-",
            },
          ]}
        />
      </Section>

      <Section title="Top Dashboard Actions">
        <DataTable
          rows={clickRows}
          emptyText="No dashboard clicks yet."
          columns={[
            { key: "page_label", label: "Page" },
            { key: "target_label", label: "Clicked" },
            { key: "target_path", label: "Destination" },
            {
              key: "clicks",
              label: "Clicks",
              render: (row) => formatNumber(row.clicks),
            },
          ]}
        />
      </Section>

      <Section title="Dashboard Sections Reached">
        <DataTable
          rows={sectionRows}
          emptyText="No dashboard section reach data yet."
          columns={[
            { key: "page_label", label: "Page" },
            { key: "section_label", label: "Section" },
            {
              key: "sessions",
              label: "Sessions reached",
              render: (row) => formatNumber(row.sessions),
            },
          ]}
        />
      </Section>
    </>
  );
}

function CbsTemplatePreview({ variant }) {
  const isModern = variant === "modern";

  return (
    <div
      style={{
        width: 260,
        color: "#102a43",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 900,
          marginBottom: 10,
          color: "#0e5aa5",
        }}
      >
        For Roy
      </div>
      <div
        style={{
          borderRadius: isModern ? 18 : 14,
          overflow: "hidden",
          background: "#ffffff",
          border: "1px solid #d9e2ec",
          boxShadow: "0 18px 40px -24px rgba(13, 38, 90, 0.45)",
        }}
      >
        {isModern ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "13px 14px 10px",
                borderBottom: "1px solid #e7ecf3",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  fontWeight: 900,
                  background: "linear-gradient(135deg, #1552F0, #3f74ff)",
                }}
              >
                S
              </div>
              <div>
                <div style={{ fontWeight: 900, fontSize: 13 }}>SmartLend</div>
                <div style={{ color: "#6b7a90", fontSize: 10 }}>
                  Compare banks & lenders
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 5, padding: "10px 14px 0" }}>
              {[0, 1, 2, 3].map((index) => (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    height: 4,
                    borderRadius: 99,
                    background: index === 0 ? "#1552F0" : "#e7ecf3",
                  }}
                />
              ))}
            </div>
            <div style={{ padding: 14 }}>
              <div
                style={{
                  display: "inline-flex",
                  borderRadius: 999,
                  padding: "4px 8px",
                  color: "#0b3ac2",
                  background: "#eaf0ff",
                  fontSize: 10,
                  fontWeight: 900,
                }}
              >
                TAKES 10 SECONDS
              </div>
              <div
                style={{
                  marginTop: 9,
                  color: "#0b1220",
                  fontSize: 15,
                  fontWeight: 900,
                  lineHeight: 1.25,
                }}
              >
                What are you hoping to sort out today?
              </div>
              {["Home / Property financing", "Business / SME financing"].map(
                (label) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 8,
                      border: "1px solid #e7ecf3",
                      borderRadius: 12,
                      padding: "8px 10px",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 8,
                        background: "#eaf0ff",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      {label.startsWith("Home") ? "H" : "B"}
                    </span>
                    {label}
                  </div>
                )
              )}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                padding: "18px 14px 14px",
                textAlign: "center",
                color: "#fff",
                background: "linear-gradient(135deg, #667eea, #764ba2)",
              }}
            >
              <div style={{ fontWeight: 900, fontSize: 16 }}>
                Quick CBS Check
              </div>
              <div style={{ fontSize: 11, opacity: 0.95, marginTop: 4 }}>
                Get instant financing options
              </div>
              <div
                style={{
                  height: 3,
                  borderRadius: 99,
                  background: "rgba(255,255,255,0.35)",
                  marginTop: 12,
                }}
              >
                <div
                  style={{
                    width: "33%",
                    height: "100%",
                    borderRadius: 99,
                    background: "#fff",
                  }}
                />
              </div>
            </div>
            <div style={{ padding: 14 }}>
              <div
                style={{
                  display: "inline-flex",
                  borderRadius: 999,
                  padding: "4px 8px",
                  color: "#667eea",
                  background: "#f0f4ff",
                  fontSize: 10,
                  fontWeight: 900,
                }}
              >
                TAKES 10 SECONDS
              </div>
              <div
                style={{
                  marginTop: 9,
                  color: "#1a1a1a",
                  fontSize: 15,
                  fontWeight: 900,
                  lineHeight: 1.25,
                }}
              >
                What are you hoping to sort out today?
              </div>
              {["Home / Property financing", "Business / SME financing"].map(
                (label) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 8,
                      border: "2px solid #e5e7eb",
                      borderRadius: 10,
                      padding: "8px 10px",
                      background: "#fafafa",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 8,
                        background: "#f0f4ff",
                        display: "grid",
                        placeItems: "center",
                        color: "#667eea",
                      }}
                    >
                      {label.startsWith("Home") ? "H" : "B"}
                    </span>
                    {label}
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CbsTemplateVariantLabel({ variant }) {
  const normalizedVariant = String(variant || "unknown").toLowerCase();

  if (!["modern", "classic"].includes(normalizedVariant)) {
    return <span>{variant || "-"}</span>;
  }

  return (
    <Tooltip
      arrow
      placement="right"
      title={<CbsTemplatePreview variant={normalizedVariant} />}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#ffffff",
            border: "1px solid #d9e2ec",
            boxShadow: "0 24px 60px -28px rgba(13, 38, 90, 0.55)",
            p: 1.25,
            maxWidth: 290,
          },
        },
        arrow: {
          sx: {
            color: "#ffffff",
            "&:before": {
              border: "1px solid #d9e2ec",
            },
          },
        },
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 999,
          padding: "4px 9px",
          background: normalizedVariant === "modern" ? "#eaf0ff" : "#f0e8ff",
          color: normalizedVariant === "modern" ? "#0b3ac2" : "#5b21b6",
          fontWeight: 900,
          cursor: "help",
        }}
      >
        {variant}
      </span>
    </Tooltip>
  );
}

function CbsCampaignAnalyticsTab({ cbs }) {
  const questionnaire = cbs?.questionnaire || {};
  const postCode = cbs?.post_code || {};
  const postQuestionnaire = cbs?.post_questionnaire || {};
  const questionnaireTotals = questionnaire?.totals || {};
  const postCodeTotals = postCode?.totals || {};
  const postQuestionnaireTotals = postQuestionnaire?.totals || {};
  const templateRows = questionnaire?.template_performance || [];
  const dropoutRows = questionnaire?.dropoffs_by_screen || [];
  const answerRows = questionnaire?.answer_distribution || [];
  const nextPageRows = postCode?.next_pages || [];
  const lastSeenRows = postCode?.last_seen_pages || [];
  const productRows = postCode?.product_exploration || [];
  const actionRows = postCode?.actions || [];
  const postQuestionnaireFormRows = postQuestionnaire?.form_performance || [];
  const postQuestionnaireDropoffRows = postQuestionnaire?.dropoffs_by_form || [];
  const postQuestionnaireSubmissionRows =
    postQuestionnaire?.recent_submissions || [];

  return (
    <>
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(170px, 1fr))"
        gap="12px"
        marginTop="24px"
      >
        <MetricCard
          label="Questionnaire starts"
          value={formatNumber(questionnaireTotals.started_sessions)}
        />
        <MetricCard
          label="Completion rate"
          value={formatPercent(questionnaireTotals.completion_rate)}
          hint={`${formatNumber(questionnaireTotals.completed_sessions)} completed`}
        />
        <MetricCard
          label="Drop-off rate"
          value={formatPercent(questionnaireTotals.dropoff_rate)}
          hint={`${formatNumber(questionnaireTotals.dropoff_sessions)} inactive before finish`}
        />
        <MetricCard
          label="Code shown"
          value={formatNumber(postCodeTotals.code_shown_sessions)}
        />
        <MetricCard
          label="Lost after code"
          value={formatPercent(postCodeTotals.left_after_code_rate)}
          hint={`${formatNumber(postCodeTotals.left_after_code_sessions)} sessions`}
        />
        <MetricCard
          label="Uploaded report"
          value={formatNumber(postCodeTotals.uploaded_report_sessions)}
        />
      </Box>

      <Section title="Questionnaire Template Performance">
        <DataTable
          rows={templateRows}
          emptyText="No CBS questionnaire starts tracked yet."
          columns={[
            {
              key: "variant",
              label: "Template",
              render: (row) => <CbsTemplateVariantLabel variant={row.variant} />,
            },
            {
              key: "started_sessions",
              label: "Started",
              render: (row) => formatNumber(row.started_sessions),
            },
            {
              key: "completed_sessions",
              label: "Completed",
              render: (row) => formatNumber(row.completed_sessions),
            },
            {
              key: "completion_rate",
              label: "Completion",
              render: (row) => formatPercent(row.completion_rate),
            },
            {
              key: "dropoff_sessions",
              label: "Drop-offs",
              render: (row) => formatNumber(row.dropoff_sessions),
            },
            {
              key: "avg_completion_seconds",
              label: "Avg. completion",
              render: (row) => formatSeconds(row.avg_completion_seconds),
            },
          ]}
        />
      </Section>

      <Section title="Where Questionnaire Users Drop Off">
        <DataTable
          rows={dropoutRows}
          emptyText="No inactive questionnaire drop-offs yet."
          columns={[
            {
              key: "variant",
              label: "Template",
              render: (row) => <CbsTemplateVariantLabel variant={row.variant} />,
            },
            { key: "screen_label", label: "Last screen seen" },
            {
              key: "sessions",
              label: "Sessions",
              render: (row) => formatNumber(row.sessions),
            },
            {
              key: "percent",
              label: "Share of starts",
              render: (row) => formatPercent(row.percent),
            },
          ]}
        />
      </Section>

      <Section title="Questionnaire Answers">
        <DataTable
          rows={answerRows}
          emptyText="No questionnaire answers tracked yet."
          columns={[
            {
              key: "variant",
              label: "Template",
              render: (row) => <CbsTemplateVariantLabel variant={row.variant} />,
            },
            { key: "question_label", label: "Question" },
            { key: "answer_label", label: "Answer" },
            {
              key: "sessions",
              label: "Sessions",
              render: (row) => formatNumber(row.sessions),
            },
            {
              key: "percent",
              label: "Share for question",
              render: (row) => formatPercent(row.percent),
            },
          ]}
        />
      </Section>

      <Section title="Secondary Post-CBS Questionnaire">
        <div style={{ padding: 16 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <MetricCard
              label="Post-CBS starts"
              value={formatNumber(postQuestionnaireTotals.started_sessions)}
            />
            <MetricCard
              label="Submitted"
              value={formatNumber(postQuestionnaireTotals.submitted_sessions)}
              hint={`${formatPercent(postQuestionnaireTotals.completion_rate)} completion`}
            />
            <MetricCard
              label="Drop-off"
              value={formatPercent(postQuestionnaireTotals.dropoff_rate)}
              hint={`${formatNumber(postQuestionnaireTotals.dropoff_sessions)} inactive before submit`}
            />
            <MetricCard
              label="Avg. time here"
              value={formatSeconds(postQuestionnaireTotals.avg_completion_seconds)}
              hint="Completed submissions only"
            />
            <MetricCard
              label="Saved rows"
              value={formatNumber(postQuestionnaireTotals.saved_submissions)}
            />
          </div>

          <DataTable
            rows={postQuestionnaireFormRows}
            emptyText="No post-CBS questionnaire starts tracked yet."
            columns={[
              { key: "form_label", label: "Form" },
              {
                key: "started_sessions",
                label: "Started",
                render: (row) => formatNumber(row.started_sessions),
              },
              {
                key: "submitted_sessions",
                label: "Submitted",
                render: (row) => formatNumber(row.submitted_sessions),
              },
              {
                key: "completion_rate",
                label: "Completion",
                render: (row) => formatPercent(row.completion_rate),
              },
              {
                key: "dropoff_sessions",
                label: "Drop-offs",
                render: (row) => formatNumber(row.dropoff_sessions),
              },
              {
                key: "avg_completion_seconds",
                label: "Avg. completion",
                render: (row) => formatSeconds(row.avg_completion_seconds),
              },
            ]}
          />
        </div>
      </Section>

      <Section title="Secondary Post-CBS Drop-Offs">
        <DataTable
          rows={postQuestionnaireDropoffRows}
          emptyText="No inactive post-CBS questionnaire drop-offs yet."
          columns={[
            { key: "form_label", label: "Form" },
            {
              key: "sessions",
              label: "Sessions",
              render: (row) => formatNumber(row.sessions),
            },
            {
              key: "percent",
              label: "Share of starts",
              render: (row) => formatPercent(row.percent),
            },
          ]}
        />
      </Section>

      <Section title="Recent Secondary Post-CBS Submissions">
        <DataTable
          rows={postQuestionnaireSubmissionRows}
          emptyText="No saved post-CBS submissions yet."
          columns={[
            { key: "submitted_at", label: "Submitted at" },
            { key: "form_label", label: "Form" },
            { key: "user_name", label: "Name" },
            { key: "user_email", label: "Email" },
            {
              key: "duration_seconds",
              label: "Time here",
              render: (row) => formatSeconds(row.duration_seconds),
            },
            {
              key: "input_payload",
              label: "Inputs",
              render: (row) => <PayloadBlock value={row.input_payload} />,
            },
            {
              key: "questionnaire_answers",
              label: "First questionnaire",
              render: (row) => <PayloadBlock value={row.questionnaire_answers} />,
            },
          ]}
        />
      </Section>

      <Section title="After CBS Code Is Shown">
        <div style={{ padding: 16 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
              alignItems: "stretch",
            }}
          >
            <FlowColumn
              title="Next page after code"
              rows={nextPageRows}
              emptyText="No post-code navigation away from CBS yet."
            />
            <FlowColumn
              title="Last point seen"
              rows={lastSeenRows}
              emptyText="No inactive post-code sessions older than 30 minutes."
            />
          </div>
        </div>
      </Section>

      <Section title="Post-Code Product Exploration">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 12,
            padding: 16,
          }}
        >
          {productRows.map((row) => (
            <div
              key={row.key}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: 14,
                background: "#fbfdff",
              }}
            >
              <div style={{ color: "#102a43", fontWeight: 800 }}>
                {row.label}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <PercentBar value={row.percent} />
                <strong style={{ color: "#0e5aa5" }}>{formatPercent(row.percent)}</strong>
              </div>
              <div style={{ color: "#627d98", fontSize: 12, marginTop: 8 }}>
                {formatNumber(row.sessions)} post-code sessions
              </div>
            </div>
          ))}
          {!productRows.length ? (
            <div style={{ color: "#7b8794" }}>No post-code product exploration yet.</div>
          ) : null}
        </div>
      </Section>

      <Section title="Post-Code Actions">
        <DataTable
          rows={actionRows}
          emptyText="No CBS code actions tracked yet."
          columns={[
            { key: "label", label: "Action" },
            {
              key: "sessions",
              label: "Sessions",
              render: (row) => formatNumber(row.sessions),
            },
            {
              key: "percent",
              label: "Share after code",
              render: (row) => formatPercent(row.percent),
            },
          ]}
        />
      </Section>
    </>
  );
}

function UserJourneyAnalytics() {
  const history = useHistory();
  const today = useMemo(() => dateInputValue(), []);
  const [days, setDays] = useState(30);
  const [rangeMode, setRangeMode] = useState("preset");
  const [selectedDate, setSelectedDate] = useState(today);
  const [startDate, setStartDate] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return dateInputValue(start);
  });
  const [endDate, setEndDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");

  const summaryQuery = useMemo(() => {
    const params = new URLSearchParams();

    if (rangeMode === "day") {
      params.set("date", selectedDate);
      return params.toString();
    }

    if (rangeMode === "range") {
      params.set("start_date", startDate);
      params.set("end_date", endDate);
      return params.toString();
    }

    params.set("days", days);
    return params.toString();
  }, [days, endDate, rangeMode, selectedDate, startDate]);

  const fetchSummary = useCallback(async () => {
    const token = Cookies.get("token");
    const userAccountType = Cookies.get("user_account_type");

    if (!token) {
      history.push("/auth/signin");
      return;
    }

    if (userAccountType && userAccountType !== "admin") {
      history.push(userAccountType === "borrower" ? "/b-dashboard" : "/dashboard");
      return;
    }

    try {
      if (rangeMode === "range" && startDate > endDate) {
        throw new Error("Start date cannot be after end date.");
      }

      setLoading(true);
      setError("");
      const response = await fetch(
        `${API_BASE}/analytics/user-journey/summary?${summaryQuery}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.error || "Unable to load analytics");
      }

      setSummary(json);
    } catch (fetchError) {
      setError(fetchError.message || "Unable to load analytics");
    } finally {
      setLoading(false);
    }
  }, [endDate, history, rangeMode, startDate, summaryQuery]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const authDropOffRows = useMemo(
    () =>
      (summary?.drop_off_candidates || []).filter((row) => row.area === "auth"),
    [summary]
  );

  const borrowerDropOffRows = useMemo(
    () =>
      (summary?.drop_off_candidates || []).filter(
        (row) => row.area === "borrower" || row.area === "dashboard"
      ),
    [summary]
  );

  const pageRows = summary?.page_stats || [];
  const landingSectionRows = summary?.landing_sections || [];
  const clickRows = summary?.top_clicks || [];
  const apiErrorRows = summary?.api_errors || [];
  const apiErrorDetailRows = summary?.api_error_details || [];
  const areaRows = summary?.area_stats || [];
  const totals = summary?.totals || {};
  const dashboardSummary = summary?.dashboard_summary || {};
  const cbsCampaignSummary = summary?.cbs_campaign_summary || {};

  return (
    <Box
      style={{
        height: "calc(100vh - 72px)",
        background: "#f5f7fa",
        padding: "28px 32px 48px",
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap="16px"
        flexWrap="wrap"
      >
        <div>
          <Typography
            variant="h4"
            style={{
              color: "#102a43",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 900,
              fontSize: 28,
            }}
          >
            User Journey Analytics
          </Typography>
          <Typography
            style={{
              color: "#52606d",
              fontFamily: "Poppins, sans-serif",
              marginTop: 4,
            }}
          >
            Dashboard behavior first, with the legacy all-pages journey preserved.
          </Typography>
        </div>
        <Box display="flex" gap="10px" alignItems="center" flexWrap="wrap">
          <FormControl size="small" style={{ width: 150, background: "#fff" }}>
            <InputLabel id="analytics-mode-label">View</InputLabel>
            <Select
              labelId="analytics-mode-label"
              value={rangeMode}
              label="View"
              onChange={(event) => setRangeMode(event.target.value)}
              style={{ color: "black" }}
            >
              <MenuItem value="preset">Preset</MenuItem>
              <MenuItem value="day">Specific day</MenuItem>
              <MenuItem value="range">Date range</MenuItem>
            </Select>
          </FormControl>

          {rangeMode === "preset" ? (
            <FormControl size="small" style={{ width: 150, background: "#fff" }}>
              <InputLabel id="analytics-range-label">Range</InputLabel>
              <Select
                labelId="analytics-range-label"
                value={days}
                label="Range"
                onChange={(event) => setDays(event.target.value)}
                style={{ color: "black" }}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={60}>Last 60 days</MenuItem>
                <MenuItem value={120}>Last 120 days</MenuItem>
              </Select>
            </FormControl>
          ) : null}

          {rangeMode === "day" ? (
            <TextField
              size="small"
              type="date"
              label="Day"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />
          ) : null}

          {rangeMode === "range" ? (
            <>
              <TextField
                size="small"
                type="date"
                label="Start"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={dateFieldSx}
              />
              <TextField
                size="small"
                type="date"
                label="End"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={dateFieldSx}
              />
            </>
          ) : null}

          <Button variant="contained" onClick={fetchSummary}>
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" alignItems="center" gap="12px" marginTop="28px">
          <CircularProgress size={22} />
          <Typography style={{ color: "#52606d" }}>Loading analytics...</Typography>
        </Box>
      ) : error ? (
        <Box
          style={{
            marginTop: 24,
            background: "#fff5f5",
            border: "1px solid #ffbdbd",
            color: "#9b1c1c",
            borderRadius: 8,
            padding: 16,
          }}
        >
          {error}
        </Box>
      ) : (
        <>
          <Box display="flex" gap="10px" flexWrap="wrap" marginTop="24px">
            {[
              ["dashboard", "Dashboard behavior"],
              ["cbs", "CBS questionnaire"],
              ["legacy", "All-pages journey"],
            ].map(([key, label]) => (
              <Button
                key={key}
                variant={activeTab === key ? "contained" : "outlined"}
                onClick={() => setActiveTab(key)}
                style={{
                  borderRadius: 8,
                  textTransform: "none",
                  fontWeight: 800,
                  background: activeTab === key ? "#0e5aa5" : "#ffffff",
                  color: activeTab === key ? "#ffffff" : "#0e5aa5",
                  borderColor: "#0e5aa5",
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          {activeTab === "dashboard" ? (
            <DashboardBehaviorTab dashboard={dashboardSummary} />
          ) : activeTab === "cbs" ? (
            <CbsCampaignAnalyticsTab cbs={cbsCampaignSummary} />
          ) : (
            <>
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))"
                gap="12px"
                marginTop="24px"
              >
                {[
                  ["Visitors", formatNumber(totals.visitors)],
                  ["Sessions", formatNumber(totals.sessions)],
                  ["Page views", formatNumber(totals.page_views)],
                  ["Clicks", formatNumber(totals.clicks)],
                  ["API errors", formatNumber(totals.api_errors)],
                  ["Avg. time", formatSeconds(totals.avg_time_seconds)],
                ].map(([label, value]) => (
                  <MetricCard key={label} label={label} value={value} />
                ))}
              </Box>

          <Section title="Journey Health">
            <DataTable
              rows={areaRows}
              columns={[
                {
                  key: "area",
                  label: "Area",
                  render: (row) => areaLabels[row.area] || row.area,
                },
                {
                  key: "page_views",
                  label: "Page views",
                  render: (row) => formatNumber(row.page_views),
                },
                {
                  key: "clicks",
                  label: "Clicks",
                  render: (row) => formatNumber(row.clicks),
                },
                {
                  key: "api_errors",
                  label: "API errors",
                  render: (row) => formatNumber(row.api_errors),
                },
                {
                  key: "avg_time_seconds",
                  label: "Avg. time",
                  render: (row) => formatSeconds(row.avg_time_seconds),
                },
              ]}
            />
          </Section>

          <Section title="Pages To Investigate">
            <DataTable
              rows={pageRows}
              columns={[
                {
                  key: "area",
                  label: "Area",
                  render: (row) => areaLabels[row.area] || row.area,
                },
                { key: "page_path", label: "Page" },
                {
                  key: "page_views",
                  label: "Views",
                  render: (row) => formatNumber(row.page_views),
                },
                {
                  key: "exits",
                  label: "Exits",
                  render: (row) => formatNumber(row.exits),
                },
                {
                  key: "avg_time_seconds",
                  label: "Avg. time",
                  render: (row) => formatSeconds(row.avg_time_seconds),
                },
                {
                  key: "avg_scroll_depth",
                  label: "Avg. scroll",
                  render: (row) =>
                    row.avg_scroll_depth ? `${Math.round(row.avg_scroll_depth)}%` : "-",
                },
                {
                  key: "api_errors",
                  label: "Errors",
                  render: (row) => formatNumber(row.api_errors),
                },
              ]}
            />
          </Section>

          <Section title="Auth Drop-Off Candidates">
            <DataTable
              rows={authDropOffRows}
              emptyText="No auth sessions currently look abandoned."
              columns={[
                { key: "page_path", label: "Last page" },
                {
                  key: "sessions",
                  label: "Sessions",
                  render: (row) => formatNumber(row.sessions),
                },
                { key: "last_seen_at", label: "Last seen" },
              ]}
            />
          </Section>

          <Section title="Borrower Dashboard Drop-Off Candidates">
            <DataTable
              rows={borrowerDropOffRows}
              emptyText="No borrower dashboard sessions currently look abandoned."
              columns={[
                { key: "page_path", label: "Last page" },
                {
                  key: "sessions",
                  label: "Sessions",
                  render: (row) => formatNumber(row.sessions),
                },
                { key: "last_seen_at", label: "Last seen" },
              ]}
            />
          </Section>

          <Section title="Landing Sections Reached">
            <DataTable
              rows={landingSectionRows}
              columns={[
                { key: "page_path", label: "Landing page" },
                { key: "section_label", label: "Section" },
                {
                  key: "sessions",
                  label: "Sessions reached",
                  render: (row) => formatNumber(row.sessions),
                },
              ]}
            />
          </Section>

          <Section title="Top Clicks">
            <DataTable
              rows={clickRows}
              columns={[
                {
                  key: "area",
                  label: "Area",
                  render: (row) => areaLabels[row.area] || row.area,
                },
                { key: "page_path", label: "Page" },
                { key: "target_label", label: "Clicked" },
                { key: "target_path", label: "Destination" },
                {
                  key: "clicks",
                  label: "Clicks",
                  render: (row) => formatNumber(row.clicks),
                },
              ]}
            />
          </Section>

          <Section title="API Errors">
            <DataTable
              rows={apiErrorRows}
              emptyText="No tracked API errors in this range."
              columns={[
                {
                  key: "area",
                  label: "Area",
                  render: (row) => areaLabels[row.area] || row.area,
                },
                { key: "page_path", label: "Page" },
                {
                  key: "api_path",
                  label: "API",
                  render: (row) => `${row.api_method || "GET"} ${row.api_path}`,
                },
                { key: "api_status", label: "Status" },
                {
                  key: "errors",
                  label: "Errors",
                  render: (row) => formatNumber(row.errors),
                },
              ]}
            />
          </Section>

          <Section title="API Error Payloads & Responses">
            <DataTable
              rows={apiErrorDetailRows}
              emptyText="No actionable API error payloads in this range."
              columns={[
                { key: "occurred_at", label: "Time" },
                {
                  key: "area",
                  label: "Area",
                  render: (row) => areaLabels[row.area] || row.area,
                },
                { key: "page_path", label: "Page" },
                {
                  key: "api_path",
                  label: "API",
                  render: (row) => `${row.api_method || "GET"} ${row.api_path}`,
                },
                { key: "api_status", label: "Status" },
                {
                  key: "session_id",
                  label: "Visitor / User",
                  render: (row) =>
                    row.user_id
                      ? `User ${row.user_id}`
                      : row.visitor_id || row.session_id || "-",
                },
                {
                  key: "request_payload",
                  label: "Request payload",
                  render: (row) => <PayloadBlock value={row.request_payload} />,
                },
                {
                  key: "response_payload",
                  label: "Response",
                  render: (row) => (
                    <PayloadBlock
                      value={
                        row.network_error
                          ? { network_error: row.network_error }
                          : row.response_payload
                      }
                    />
                  ),
                },
              ]}
            />
          </Section>
            </>
          )}
        </>
      )}
    </Box>
  );
}

export default UserJourneyAnalytics;
